/**
 * A card's printings as the scanner offers them. Catalogue variant prices are whole USD *cents*
 * (85113 = US$851.13), in every game.
 */
import { stripLanguageSuffix } from '../../utils/conditions'

/** A catalogue variant from card metadata (or a lookup variant); `price` is USD cents, not dollars. */
export interface MarketVariant { printing?: string; condition?: string; price?: unknown }

/**
 * Market prices per printing, in USD cents. Language-tagged duplicates are one choice: Japanese
 * cards often list both "Holofoil - Japanese" (priced) and "Holofoil" (empty), which the backend
 * already treats as the same printing; it shows once as "Holofoil" and is recorded as the priced
 * one, so the store's product prices against it.
 */
export function marketByPrinting(variants: MarketVariant[]): Array<{ printing: string; label: string; byCondition: Map<string, number> }> {
  const out = new Map<string, Map<string, number>>()
  for (const v of variants) {
    const printing = String(v.printing || 'Normal')
    if (!out.has(printing)) out.set(printing, new Map())
    const cents = Math.round(Number(v.price || 0))
    if (cents > 0 && v.condition && !out.get(printing)!.has(v.condition)) out.get(printing)!.set(v.condition, cents)
  }
  const groups = new Map<string, { printing: string; label: string; byCondition: Map<string, number> }>()
  for (const [printing, byCondition] of out) {
    const label = stripLanguageSuffix(printing).trim() || printing
    const key = label.toLowerCase()
    const seen = groups.get(key)
    if (!seen || byCondition.size > seen.byCondition.size) groups.set(key, { printing, label, byCondition })
  }
  return [...groups.values()]
}

/** The printings to choose from (value recorded, label shown), duplicates folded as above. */
export function printingOptions(variants: MarketVariant[]): Array<{ value: string; label: string }> {
  return marketByPrinting(variants).filter((m) => m.printing.toLowerCase() !== 'sealed').map((m) => ({ value: m.printing, label: m.label }))
}

/** "Holofoil - Japanese" and "Holofoil" name the same printing. */
export const samePrinting = (a: string, b: string) =>
  a === b || stripLanguageSuffix(a).trim().toLowerCase() === stripLanguageSuffix(b).trim().toLowerCase()

/**
 * A line's printing as one of the choices: its first printing until one is chosen, and a line saved
 * before the fold maps to its folded choice.
 */
export function choosePrinting(options: Array<{ value: string; label: string }>, printing: string | null): string {
  if (!options.length) return printing ?? 'Normal'
  if (!printing) return options[0].value
  return options.find((o) => o.value === printing || samePrinting(o.value, printing))?.value ?? printing
}
